<script>
  /* eslint-disable no-undef */
  import cloneDeep from 'lodash.clonedeep'

  import FormMemberBase from './extends/FormMemberBase'

  import FormMeta from './FormMeta'

  export default {
    name: 'FormCollection',

    components: {
      FormMeta,
    },

    extends: FormMemberBase,

    props: {
      config: {
        type: Object,
        required: true,
      },
      showRemove: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['list-remove'],

    data() {
      const { type, children } = this.config
      let model

      if (type === 'object' && typeof this.value !== 'object') {
        model = {}
      } else if ((type === 'list' || type === 'array') && !Array.isArray(this.value)) {
        model = new Array(children.length).fill(undefined)
      } else {
        model = cloneDeep(this.value)
      }

      return {
        model,
      }
    },

    methods: {
      emitListRemove() {
        this.$emit('list-remove')
      },

      handleAddListItem() {
        this.model.push(undefined) // push() != push(undefined)
      },

      handleRemoveListItem(i) {
        this.model.splice(i, 1)
      },

      renderLayout(children, layout) {
        const Layout = this.getLayout(layout)

        return (
          <Layout
            {...{
              ...layout.props,
              ...layout.on,
            }}
          >
            {this.renderObject(children)}
          </Layout>
        )
      },

      renderObject(children) {
        return (
          children.map((child, i) => {
            const {
              name, show, type, layout, component,
            } = child

            if (!this.showChild(show)) {
              return undefined
            }

            if (type === 'layout') {
              return this.renderLayout(child.children, layout)
            }

            if (component) {
              return (
                <FormMeta
                  key={i}
                  config={child}
                  value={this.model[name]}
                  onInput={v => {
                    this.model[name] = v
                  }}
                />
              )
            }

            return (
              <FormCollection
                key={i}
                config={child}
                value={this.model[name]}
                onInput={v => {
                  this.model[name] = v
                }}
              />
            )
          })
        )
      },

      renderArray(children) {
        return (
          children.map((child, i) => {
            const {
              show, type, component, layout,
            } = child

            if (!this.showChild(show)) {
              return undefined
            }

            if (type === 'layout') {
              return this.renderLayout(child.children, layout)
            }

            if (component) {
              return (
                <FormMeta
                  key={i}
                  config={child}
                  value={this.model[i]}
                  onInput={v => {
                    this.model[i] = v
                  }}
                />
              )
            }

            return (
              <FormCollection
                key={i}
                config={child}
                value={this.model[i]}
                onInput={v => {
                  this.model[i] = v
                }}
              />
            )
          })
        )
      },

      renderList(children) {
        const firstChild = children[0]
        const {
          show, type, component, layout,
        } = firstChild

        return (
          this.model.map((item, i) => {
            if (!this.showChild(show)) {
              return undefined
            }

            if (type === 'layout') {
              return this.renderLayout(firstChild.children, layout)
            }

            if (component) {
              return (
                <FormMeta
                  showRemove
                  key={i}
                  config={firstChild}
                  value={this.model[i]}
                  onInput={v => {
                    this.model[i] = v
                  }}
                  onRemove={() => {
 this.handleRemoveListItem(i)
}}
                />
              )
            }

            return (
              <FormCollection
                showRemove
                key={i}
                config={firstChild}
                value={this.model[i]}
                onInput={v => {
                  this.model[i] = v
                }}
                onListRemove={() => {
 this.handleRemoveListItem(i)
}}
              />
            )
          })
        )
      },

      showChild(show) {
        if (typeof show === 'function') {
          return show(this.model)
        }

        return show
      },
    },

    render() {
      const {
        required, type, layout, children,
      } = this.config
      const Layout = this.getLayout(layout)
      let collection
      if (type === 'layout') {
        collection = this.renderLayout(children, layout)
      } else if (type === 'object') {
        collection = this.renderObject(children)
      } else if (type === 'array') {
        collection = this.renderArray(children)
      } else if (type === 'list') {
        collection = this.renderList(children)
      }

      return (
        <Layout
          {...{
            ...layout.props,
            ...layout.on,
          }}
          required={required}
          showAdd={type === 'list'}
          showRemove={this.showRemove}
          onAdd={this.handleAddListItem}
          onRemove={this.emitListRemove}
        >
          {collection}
          {this.$slots.default?.()}
        </Layout>
      )
    },
  }
</script>
